/**
 * @prettier
 * @author Alexander Nagelberg
 *
 **/
import React, { useEffect, useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { useLocation } from 'react-router-dom'

import LgyLoadingIcon from 'app/components/util/lgy-loading-icon/LgyLoadingIcon'
import LgyAlertDialog from 'app/components/util/lgy-alert-dialog/LgyAlertDialog'
import RefundRecordsTable from '../components/RefundRecordsTable'
import DateRangePicker from '../components/DateRangePicker'
import StatusFilter from '../components/StatusFilter'
import FailureFilter from '../components/FailureFilter'
import RefundTotals from '../components/RefundTotals'
import { loadRefunds, clearRefunds, fetchRefundTotals, exportResults } from '../actions/fundingfeeActions'

import '../style/fundingfee.scss'

// TODO: can this be configured somewhere?
const initialPageSize = 100

const RefundProcessingContainer = ({ fundingFee, loadRefunds, clearRefunds, fetchRefundTotals, exportResults }) => {
  const { search } = useLocation()
  const [hasRefunds, setHasRefunds] = useState(false)
  const { to, from, status, failure } = fundingFee?.filter

  const getArgs = useCallback(() => {
    const query = new URLSearchParams(search)
    return {
      from: query.get('from') ? query.get('from') : from,
      to: query.get('to') ? query.get('to') : to,
      status: query.get('status') ? query.get('status') : status,
      failure: query.get('failure') ? query.get('failure') : failure,
      page: 0,
      limit: initialPageSize
    }
  }, [search, from, to, status, failure])

  // callback for table component, update on paging changes
  const updatePage = useCallback(
    pageInfo => {
      // Only loads refunds when filter selected
      const args = getArgs()
      if (args.status || args.failure || (args.from && args.to)) {
        setHasRefunds(true)
        loadRefunds({ ...args, ...pageInfo })
      }
    },
    [getArgs, loadRefunds]
  )

  // Fetch refunds whenever filters in URI change & clear it when filters don't exist
  useEffect(() => {
    const args = getArgs()
    if (args.status || args.failure || (args.from && args.to)) {
      setHasRefunds(true)
      fetchRefundTotals(args)
    } else {
      setHasRefunds(false)
      clearRefunds()
    }
  }, [fetchRefundTotals, clearRefunds, getArgs])

  return (
    <div className='lgy-funding-fee-refund-processing'>
      <div className='lgy-funding-fee-alerts'>
        {fundingFee.success && (
          <LgyAlertDialog type='success' heading='Success' paragraph>
            Successfully updated
          </LgyAlertDialog>
        )}
        {fundingFee.error && (
          <LgyAlertDialog type='error' heading='Error' paragraph>
            Error updating
          </LgyAlertDialog>
        )}
      </div>
      <div>
        <div className='usa-grid'>
          <div className='usa-width-one-half'>
            <DateRangePicker />
          </div>
          <div className='usa-width-one-fourth'>
            <StatusFilter />
          </div>
          <div className='usa-width-one-fourth'>
            <FailureFilter />
          </div>
        </div>
        {hasRefunds && (fundingFee.fetching || fundingFee.posting) && <LgyLoadingIcon />}
        {fundingFee.refunds && (
          <div className='lgy-funding-fee-records-table'>
            <RefundTotals />
            <button onClick={() => exportResults({ ...getArgs(), page: null, limit: null })}>Export CSV</button>
            <RefundRecordsTable
              updatePage={updatePage}
              initialPageSize={initialPageSize}
              refunds={fundingFee.refunds}
              totalRefunds={fundingFee.refundsInfo.totalRefunds}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = state => ({ fundingFee: state.fundingFee })

export default connect(mapStateToProps, {
  loadRefunds,
  clearRefunds,
  fetchRefundTotals,
  exportResults
})(RefundProcessingContainer)
